import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  ChefHat,
  ChevronDown,
  Clock3,
  ContactRound,
  DollarSign,
  Eye,
  Filter,
  Gauge,
  Info,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  UsersRound,
  Utensils,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import {
  getAdminUsageQualityWeekly,
  type AdminUsageQualityAIUsage,
  type AdminUsageQualityAIUsageDaily,
  type AdminUsageQualityCohortGroup,
  type AdminUsageQualityDayDetail,
  type AdminUsageQualityMealDetail,
  type AdminUsageQualityMealPreviewSuggestion,
  type AdminUsageQualityPractitioner,
  type AdminUsageQualityRecipeDetail,
  type AdminUsageQualityRecipeNutrition,
  type AdminUsageQualitySampleMealPlan,
  type AdminUsageQualityWeeklyResponse,
} from '@/lib/api/auth';
import { getAIMealPlanCohortLabel } from '@/constants/ai-model-options';
import { getCountryDisplayName } from '@/utils/countryCurrency';

const SAMPLE_LIMIT_OPTIONS = [8, 12, 20, 25] as const;
const PRACTITIONER_MEAL_PLAN_LIMIT = 250;
const QUICK_WINDOWS = [
  { label: '1 day', days: 1 },
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
] as const;
type UsageQualityTab = 'ai' | 'activity' | 'quality' | 'cohorts';
type PractitionerAccountFilter = 'all' | AdminUsageQualityPractitioner['accountClassification'];
type PractitionerStatusFilter = 'all' | 'genuine' | 'activated' | 'meaningful' | 'inactive';
type PractitionerRecencyFilter = '7' | '30' | 'all';

const ACCOUNT_CLASSIFICATION_LABELS: Record<AdminUsageQualityPractitioner['accountClassification'], string> = {
  external: 'External',
  internal: 'Internal',
  test_demo: 'Test / demo',
  duplicate: 'Duplicate',
};

const SOURCE_LABELS: Record<string, string> = {
  ai_openai: 'AI OpenAI',
  ai_deepseek: 'AI DeepSeek',
  spoonacular: 'Spoonacular',
  manual: 'Manual',
  generic: 'Cohort Meal',
  unknown: 'Unknown',
  finalized_manual: 'Finalized Manual',
  finalized_automated: 'Finalized Automated',
  finalized_ai_assisted: 'Finalized AI',
  finalized_unknown: 'Finalized Unknown',
};

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultFromDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return toDateInputValue(date);
};

const getTodayDate = (): string => toDateInputValue(new Date());

const hasPractitionerActivityWithinDays = (
  practitioner: AdminUsageQualityPractitioner,
  days: number,
  now = Date.now(),
): boolean => {
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  return [
    practitioner.lastActivityAt,
    practitioner.lastLoginAt,
    ...(practitioner.activities || []).map((activity) => activity.occurredAt),
  ].some((value) => {
    if (!value) return false;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) && timestamp >= cutoff;
  });
};

// Read-only: detects whether the current date range equals a preset quick window (for highlighting only).
const matchesQuickWindow = (fromDate: string, toDate: string, days: number): boolean => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - days + 1);
  return toDate === toDateInputValue(to) && fromDate === toDateInputValue(from);
};

const formatNumber = (value: number | null | undefined): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatCompactNumber = (value: number | null | undefined): string =>
  new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const formatUsd = (value: number | null | undefined, hasUnknownCost = false): string => {
  const numeric = Number(value || 0);
  const digits = numeric > 0 && numeric < 1 ? 4 : 2;
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(numeric);
  return hasUnknownCost ? `${formatted}+` : formatted;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatUtcDateKey = (value: string | null | undefined): string => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '-';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatUsageWindowRange = (
  window: AdminUsageQualityWeeklyResponse['data']['window'] | null | undefined,
): string => {
  if (!window) {
    return 'AI spend, practitioner activity & meal-plan quality';
  }
  const dateKeys = window.utcDateKeys?.filter((dateKey) => /^\d{4}-\d{2}-\d{2}$/.test(dateKey));
  if (dateKeys?.length) {
    return `Showing ${formatUtcDateKey(dateKeys[0])} - ${formatUtcDateKey(dateKeys[dateKeys.length - 1])}`;
  }
  return `Showing ${formatDate(window.start)} - ${formatDate(new Date(new Date(window.end).getTime() - 1).toISOString())}`;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatChartDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const formatSource = (source: string): string =>
  SOURCE_LABELS[source] || source.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatCountry = (country: string | null | undefined): string =>
  getCountryDisplayName(country) || 'Country not set';

const getInitials = (name: string | null | undefined): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// Deterministic soft avatar tint so identities are easy to scan without adding noise.
const AVATAR_TONES = [
  'bg-[#e6f6fb] text-[#0e7fa0]',
  'bg-[#eef0ff] text-[#5a51c8]',
  'bg-[#eafaf3] text-[#0f9b6c]',
  'bg-[#fff2e3] text-[#b46b18]',
  'bg-[#fdeef4] text-[#b8477e]',
  'bg-[#eef2f6] text-[#4a5b6b]',
];

const avatarTone = (seed: string): string => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index)) % AVATAR_TONES.length;
  }
  return AVATAR_TONES[hash];
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.response?.data?.error || error.message || fallback;
  }

  return error instanceof Error ? error.message : fallback;
};

const compactTags = (values: string[], max = 4): string[] => {
  const normalized = values.map((value) => value.trim()).filter(Boolean);
  if (normalized.length <= max) return normalized;
  return [...normalized.slice(0, max), `+${normalized.length - max}`];
};

const formatMacro = (value: number | null | undefined, unit = 'g'): string =>
  Number.isFinite(Number(value)) ? `${formatNumber(Number(value))}${unit}` : '-';

const formatCalories = (value: number | null | undefined): string =>
  Number.isFinite(Number(value)) ? `${formatNumber(Number(value))} kcal` : '-';

const completionRate = (finalized: number, generated: number): number =>
  generated > 0 ? Math.round((finalized / generated) * 100) : 0;

const buildEmptyAIUsage = (): AdminUsageQualityAIUsage => ({
  calls: 0,
  promptTokens: 0,
  completionTokens: 0,
  totalTokens: 0,
  cachedPromptTokens: 0,
  uncachedPromptTokens: 0,
  reasoningTokens: 0,
  estimatedCostUsd: 0,
  hasUnknownCost: false,
  daily: [],
  quota: {
    actions: [],
    totalLimit: 0,
    totalUsed: 0,
    totalRemaining: 0,
    nextResetAt: null,
  },
});

const getAIUsage = (usage: AdminUsageQualityAIUsage | undefined | null): AdminUsageQualityAIUsage =>
  usage ?? buildEmptyAIUsage();

const hasAIUsageActivity = (row: AdminUsageQualityPractitioner): boolean => {
  const usage = getAIUsage(row.aiUsage);
  return (
    usage.calls > 0
    || usage.totalTokens > 0
    || usage.estimatedCostUsd > 0
    || usage.quota.totalUsed > 0
    || usage.quota.actions.some((action) => action.used > 0)
  );
};

const getNutritionValue = (
  nutrition: AdminUsageQualityRecipeNutrition | undefined | null,
  key: keyof AdminUsageQualityRecipeNutrition,
): number | null => {
  const value = Number(nutrition?.[key]);
  return Number.isFinite(value) ? value : null;
};

const hasMealPreviewSelection = (meal: AdminUsageQualitySampleMealPlan['mealPreview'][number]): boolean =>
  meal.hasSelectedRecipe !== false;

const buildPreviewRecipeDetail = (
  recipe: AdminUsageQualityMealPreviewSuggestion,
): AdminUsageQualityRecipeDetail => ({
  recipeId: recipe.recipeId,
  sourceId: null,
  title: recipe.title,
  description: null,
  image: null,
  generatedVia: null,
  generatedFromTitle: null,
  readyInMinutes: null,
  servings: null,
  nutritionServings: null,
  nutrition: {
    calories: recipe.calories,
    protein: recipe.protein,
    carbs: recipe.carbs,
    fat: recipe.fat,
    fibre: null,
    sodium: null,
  },
  ingredients: [],
  instructions: [],
  allergens: [],
  intoleranceTags: [],
  cuisines: [],
  mealTypes: [],
  dietLabels: [],
  isVegan: false,
  isVegetarian: false,
  isGlutenFree: false,
  isDairyFree: false,
  customized: false,
});

const getMealPreviewSuggestions = (
  sample: AdminUsageQualitySampleMealPlan,
  meal: AdminUsageQualitySampleMealPlan['mealPreview'][number],
): AdminUsageQualityMealPreviewSuggestion[] => {
  if (meal.recipeSuggestions?.length) {
    return meal.recipeSuggestions;
  }

  const detailMeal = sample.mealPlanDetail?.days
    .find((day) => day.dayNumber === meal.dayNumber)
    ?.meals.find((candidate) => candidate.mealName === meal.mealName);
  const recipes = detailMeal
    ? [...(detailMeal.selectedRecipe ? [detailMeal.selectedRecipe] : []), ...detailMeal.alternatives]
    : [];
  return recipes.map((recipe) => ({
    recipeId: recipe.recipeId,
    title: recipe.title,
    calories: recipe.nutrition.calories,
    protein: recipe.nutrition.protein,
    carbs: recipe.nutrition.carbs,
    fat: recipe.nutrition.fat,
  }));
};

const buildFallbackDayDetails = (sample: AdminUsageQualitySampleMealPlan): AdminUsageQualityDayDetail[] => {
  const byDay = new Map<number, AdminUsageQualityDayDetail>();
  for (const meal of sample.mealPreview) {
    const hasSelectedRecipe = hasMealPreviewSelection(meal);
    const recipeSuggestions = meal.recipeSuggestions || [];
    const day = byDay.get(meal.dayNumber) ?? {
      dayNumber: meal.dayNumber,
      date: null,
      notes: null,
      totals: { calories: 0, protein: 0, carbs: 0, fat: 0, fibre: null, sodium: null },
      meals: [],
    };
    day.totals.calories = Number(day.totals.calories || 0) + Number(meal.calories || 0);
    day.totals.protein = Number(day.totals.protein || 0) + Number(meal.protein || 0);
    day.totals.carbs = Number(day.totals.carbs || 0) + Number(meal.carbs || 0);
    day.totals.fat = Number(day.totals.fat || 0) + Number(meal.fat || 0);
    day.meals.push({
      mealOrder: day.meals.length,
      mealName: meal.mealName,
      mealType: '',
      mealTime: '',
      targetCalories: null,
      notes: null,
      selectedRecipeId: meal.selectedRecipeId ?? null,
      selectedRecipe: hasSelectedRecipe ? buildPreviewRecipeDetail({
        recipeId: meal.selectedRecipeId ?? null,
        title: meal.recipeTitle,
        calories: meal.calories,
        protein: meal.protein,
        carbs: meal.carbs,
        fat: meal.fat,
      }) : null,
      alternatives: recipeSuggestions
        .filter((suggestion) => !hasSelectedRecipe
          || (suggestion.recipeId !== meal.selectedRecipeId && suggestion.title !== meal.recipeTitle))
        .map((suggestion) => buildPreviewRecipeDetail(suggestion)),
    });
    byDay.set(meal.dayNumber, day);
  }
  return Array.from(byDay.values()).sort((a, b) => a.dayNumber - b.dayNumber);
};

const getSampleDetailDays = (sample: AdminUsageQualitySampleMealPlan): AdminUsageQualityDayDetail[] =>
  sample.mealPlanDetail?.days?.length ? sample.mealPlanDetail.days : buildFallbackDayDetails(sample);

const getSampleDailyAverages = (sample: AdminUsageQualitySampleMealPlan): AdminUsageQualityRecipeNutrition => {
  if (sample.mealPlanDetail?.dailyAverages) {
    return sample.mealPlanDetail.dailyAverages;
  }
  const days = getSampleDetailDays(sample);
  const denominator = days.length || 1;
  return days.reduce<AdminUsageQualityRecipeNutrition>(
    (acc, day) => ({
      calories: Number(acc.calories || 0) + Number(day.totals.calories || 0) / denominator,
      protein: Number(acc.protein || 0) + Number(day.totals.protein || 0) / denominator,
      carbs: Number(acc.carbs || 0) + Number(day.totals.carbs || 0) / denominator,
      fat: Number(acc.fat || 0) + Number(day.totals.fat || 0) / denominator,
      fibre: Number(acc.fibre || 0) + Number(day.totals.fibre || 0) / denominator,
      sodium: Number(acc.sodium || 0) + Number(day.totals.sodium || 0) / denominator,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0, fibre: 0, sodium: 0 },
  );
};

const qualityFlagsForSample = (sample: AdminUsageQualitySampleMealPlan): Array<{
  label: string;
  value: number;
  tone: string;
}> => {
  const flags = sample.mealPlanDetail?.reviewFlags;
  if (!flags) {
    return [];
  }
  return [
    { label: 'Missing selections', value: flags.missingSelectionCount, tone: 'text-[#b42318] bg-[#fff6f5] border-[#ffd7d2]' },
    { label: 'Allergen-tagged meals', value: flags.mealsWithAllergenTags, tone: 'text-[#8a4b00] bg-[#fff8eb] border-[#f8dfb6]' },
    { label: 'Intolerance-tagged meals', value: flags.mealsWithIntoleranceTags, tone: 'text-[#8a4b00] bg-[#fff8eb] border-[#f8dfb6]' },
    { label: 'Customized recipes', value: flags.customizedRecipeCount, tone: 'text-[#0f9b6c] bg-[#eafaf3] border-[#c7ecd8]' },
    { label: 'Generated alternates', value: flags.generatedRecipeCount, tone: 'text-[#49428a] bg-[#f7f5ff] border-[#d9d4f0]' },
  ];
};

// Shared minimal surface used across the redesigned page (hairline border, whisper shadow).
const PANEL = 'rounded-lg border border-[#e8eaee] bg-white shadow-[0_1px_2px_rgba(17,24,39,0.04)]';

// Stripe-style KPI band: a single divided surface instead of boxy cards.
const StatCell: React.FC<{
  label: string;
  value: number | string;
  detail?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  aside?: React.ReactNode;
  small?: boolean;
  definition?: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ label, value, detail, icon: Icon, accent, aside, small = false, definition, actionLabel = 'View details', onAction }) => {
  const content = (
    <>
    <div className="flex items-center gap-2 text-[#71767f]">
      <Icon className={`h-4 w-4 ${accent}`} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.09em]">{label}</span>
      {definition ? <Info className="h-3.5 w-3.5 text-[#a0a7b0]" aria-label={definition} /> : null}
    </div>
    <div className="flex items-end justify-between gap-3">
      <p
        className={
          small
            ? 'min-w-0 font-heading text-base font-bold leading-snug tracking-tight text-[#14151a]'
            : 'min-w-0 break-words font-heading text-[1.6rem] font-bold leading-none tracking-tight tabular-nums text-[#14151a]'
        }
      >
        {typeof value === 'number' ? formatNumber(value) : value}
      </p>
      {aside}
    </div>
    {detail ? <p className="text-xs leading-relaxed text-[#8a909a]">{detail}</p> : null}
    {onAction ? (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0e7fa0] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        {actionLabel}<ArrowRight className="h-3.5 w-3.5" />
      </span>
    ) : null}
    </>
  );

  return onAction ? (
    <button
      type="button"
      onClick={onAction}
      title={definition}
      className="group flex w-full flex-col gap-2.5 bg-white px-5 py-5 text-left transition-colors hover:bg-[#f8fbfc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0e97be] sm:px-6"
    >
      {content}
    </button>
  ) : (
    <div title={definition} className="flex flex-col gap-2.5 bg-white px-5 py-5 sm:px-6">{content}</div>
  );
};

// Gap-based grid: the panel's border colour shows through 1px gaps as clean dividers at every breakpoint.
// Column count matches the number of cells so no empty grid areas are ever exposed.
const StatBand: React.FC<{ children: React.ReactNode; cols?: 3 | 4 }> = ({ children, cols = 4 }) => (
  <div className={`${PANEL} overflow-hidden`}>
    <div
      className={
        cols === 3
          ? 'grid grid-cols-1 gap-px bg-[#eef0f3] sm:grid-cols-3'
          : 'grid grid-cols-1 gap-px bg-[#eef0f3] sm:grid-cols-2 xl:grid-cols-4'
      }
    >
      {children}
    </div>
  </div>
);

const SectionShell: React.FC<{
  id?: string;
  title: string;
  eyebrow?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, eyebrow, action, children }) => (
  <section id={id} className={`scroll-mt-24 overflow-hidden ${PANEL}`}>
    <div className="flex flex-col gap-3 border-b border-[#eef0f3] px-5 py-4 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        {eyebrow ? <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9aa1ab]">{eyebrow}</p> : null}
        <h2 className="mt-0.5 font-heading text-[15px] font-bold tracking-tight text-[#14151a]">{title}</h2>
      </div>
      {action ? <div className="w-full md:w-auto">{action}</div> : null}
    </div>
    {children}
  </section>
);

const SourceMix: React.FC<{ counts: Record<string, number> }> = ({ counts }) => {
  const entries = Object.entries(counts)
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  const total = entries.reduce((sum, [, count]) => sum + Number(count), 0);

  if (entries.length === 0 || total <= 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-5 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0e97be]"><Utensils className="h-5 w-5" /></span>
        <p className="text-sm text-[#8a909a]">No source data for this window.</p>
      </div>
    );
  }

  return (
    <div className="px-5 py-5">
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {entries.map(([source, count]) => (
          <div key={source} className="rounded-lg bg-[#f7f8fa] px-3 py-2.5 transition-colors hover:bg-[#f1f2f4]">
            <div className="flex items-center justify-between gap-3">
              <span className="truncate text-sm font-medium text-[#41545e]">{formatSource(source)}</span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-[#14151a]">
                {formatNumber(count)}
                <span className="ml-1 text-xs font-normal text-[#9aa1ab]">{Math.round((Number(count) / total) * 100)}%</span>
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#e8ecef]">
              <span
                className="block h-full rounded-full bg-[#0e97be] transition-all duration-500 ease-out"
                style={{ width: `${Math.max(4, (Number(count) / total) * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AIUsageTrendCharts: React.FC<{ usage: AdminUsageQualityAIUsage }> = ({ usage }) => {
  const chartData = usage.daily.map((day) => ({
    date: formatChartDate(day.date),
    calls: day.calls,
    tokens: day.totalTokens,
    cost: day.estimatedCostUsd,
  }));
  const hasUsage = chartData.some((day) => day.calls > 0 || day.tokens > 0 || day.cost > 0);

  if (!hasUsage) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-5 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0e97be]"><BarChart3 className="h-5 w-5" /></span>
        <p className="text-sm text-[#8a909a]">No AI token usage was logged for this window.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-5 px-5 py-5 xl:grid-cols-2">
      <div className="min-h-0">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-[#14151a]">Token volume</p>
          <Badge variant="outline" className="border-[#e8eaee] bg-[#f7f8fa] text-[#41545e]">
            {formatCompactNumber(usage.totalTokens)} tokens
          </Badge>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#eef0f3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8a909a', fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(value) => formatCompactNumber(Number(value))} tick={{ fill: '#8a909a', fontSize: 12 }} tickLine={false} axisLine={false} width={44} />
              <ChartTooltip
                formatter={(value) => [formatNumber(Number(value)), 'Tokens']}
                labelStyle={{ color: '#14151a' }}
                contentStyle={{ borderColor: '#e8eaee', borderRadius: 8 }}
              />
              <Bar dataKey="tokens" fill="#0e97be" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="min-h-0">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-[#14151a]">Estimated spend</p>
          <Badge variant="outline" className="border-[#e8eaee] bg-[#f7f8fa] text-[#41545e]">
            {formatUsd(usage.estimatedCostUsd, usage.hasUnknownCost)}
          </Badge>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="aiUsageCost" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0f9b6c" stopOpacity={0.42} />
                  <stop offset="95%" stopColor="#0f9b6c" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#eef0f3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8a909a', fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(value) => formatUsd(Number(value))} tick={{ fill: '#8a909a', fontSize: 12 }} tickLine={false} axisLine={false} width={54} />
              <ChartTooltip
                formatter={(value) => [formatUsd(Number(value)), 'USD']}
                labelStyle={{ color: '#14151a' }}
                contentStyle={{ borderColor: '#e8eaee', borderRadius: 8 }}
              />
              <Area type="monotone" dataKey="cost" stroke="#0f9b6c" strokeWidth={2} fill="url(#aiUsageCost)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const MiniAIUsageBars: React.FC<{ daily: AdminUsageQualityAIUsageDaily[] }> = ({ daily }) => {
  const points = daily.slice(-14);
  const maxTokens = Math.max(...points.map((point) => point.totalTokens), 1);

  if (points.length === 0 || points.every((point) => point.totalTokens <= 0)) {
    return <span className="text-xs text-[#a3a9b2]">No token logs</span>;
  }

  return (
    <div className="flex h-11 items-end gap-1" aria-label="Recent AI token usage">
      {points.map((point) => (
        <span
          key={point.date}
          title={`${formatChartDate(point.date)}: ${formatNumber(point.totalTokens)} tokens`}
          className="w-2 rounded-t bg-[#0e97be]"
          style={{ height: `${Math.max(12, (point.totalTokens / maxTokens) * 44)}px` }}
        />
      ))}
    </div>
  );
};

const AIQuotaChips: React.FC<{ usage: AdminUsageQualityAIUsage }> = ({ usage }) => {
  if (usage.quota.actions.length === 0) {
    return <span className="text-sm text-[#8a909a]">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {usage.quota.actions.map((action) => {
        const depleted = action.remaining <= 0 && action.limit > 0;
        const low = action.limit > 0 && !depleted && action.remaining / action.limit <= 0.25;
        const tone = depleted
          ? 'bg-[#fdecea] text-[#b42318]'
          : low
            ? 'bg-[#fff4e5] text-[#b46b18]'
            : 'bg-[#f1f2f4] text-[#41545e]';
        return (
          <span
            key={action.action}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}
          >
            <span className="text-[#9aa1ab]">{action.label}</span>
            <span className="font-semibold tabular-nums">{formatNumber(action.remaining)}/{formatNumber(action.limit)}</span>
          </span>
        );
      })}
    </div>
  );
};

const AIQuotaOverview: React.FC<{ usage: AdminUsageQualityAIUsage }> = ({ usage }) => {
  const totalPercent = usage.quota.totalLimit > 0
    ? Math.min(100, Math.round((usage.quota.totalUsed / usage.quota.totalLimit) * 100))
    : 0;

  return (
    <div className="space-y-4 px-5 py-5">
      <div className="rounded-2xl bg-gradient-to-br from-[#0e97be] to-[#0c7f9f] p-5 text-white shadow-[0_10px_30px_-14px_rgba(14,151,190,0.65)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-white/70">Rolling quota</p>
            <p className="mt-2 font-heading text-[1.9rem] font-bold leading-none tracking-tight tabular-nums">
              {formatNumber(usage.quota.totalRemaining)}
              <span className="ml-1.5 text-sm font-semibold text-white/75">left</span>
            </p>
          </div>
          <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold tabular-nums text-white">
            {formatNumber(usage.quota.totalUsed)}/{formatNumber(usage.quota.totalLimit)}
          </span>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/20">
          <span className="block h-full rounded-full bg-white transition-all duration-500" style={{ width: `${totalPercent}%` }} />
        </div>
        <p className="mt-2.5 text-xs text-white/75">Next reset {formatDateTime(usage.quota.nextResetAt)}</p>
      </div>

      <div className="space-y-2.5">
        {usage.quota.actions.length === 0 ? (
          <p className="rounded-xl bg-[#f7f8fa] px-4 py-3 text-sm text-[#8a909a]">Quota counters have not been initialized yet.</p>
        ) : (
          usage.quota.actions.map((action) => {
            const percent = action.limit > 0 ? Math.min(100, Math.round((action.used / action.limit) * 100)) : 0;
            const depleted = action.remaining <= 0 && action.limit > 0;

            return (
              <div key={action.action} className="rounded-xl bg-[#f7f8fa] px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#14151a]">{action.label}</p>
                    <p className="mt-0.5 text-xs text-[#9aa1ab]">Resets {formatDateTime(action.resetAt)}</p>
                  </div>
                  <span
                    className={
                      depleted
                        ? 'shrink-0 rounded-full bg-[#fdecea] px-2.5 py-1 text-xs font-semibold text-[#b42318]'
                        : 'shrink-0 rounded-full bg-[#eafaf3] px-2.5 py-1 text-xs font-semibold text-[#0f9b6c]'
                    }
                  >
                    {formatNumber(action.remaining)} left
                  </span>
                </div>
                <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-white">
                  <span
                    className={depleted ? 'block h-full rounded-full bg-[#e0483c] transition-all duration-500' : 'block h-full rounded-full bg-[#0f9b6c] transition-all duration-500'}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const AIUsageNutritionistTable: React.FC<{ rows: AdminUsageQualityPractitioner[] }> = ({ rows }) => {
  const activeRows = rows.filter(hasAIUsageActivity);
  const visibleRows = activeRows.length > 0 ? activeRows : rows;
  const hiddenQuietRows = activeRows.length > 0 ? rows.length - activeRows.length : 0;

  return (
    <div>
      {hiddenQuietRows > 0 ? (
        <div className="flex items-start gap-2.5 border-b border-[#eef0f3] bg-[#f7f8fa] px-5 py-3 text-sm text-[#5a6b74]">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#0e97be]" />
          <span>
            Showing <span className="font-semibold text-[#14151a]">{formatNumber(activeRows.length)}</span> nutritionists with AI activity. {formatNumber(hiddenQuietRows)} quiet nutritionists remain in the Practitioners tab.
          </span>
        </div>
      ) : null}
      <div className="max-h-[38rem] overflow-y-auto">
        {visibleRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0e97be]">
              <Search className="h-6 w-6" />
            </span>
            <p className="text-sm font-semibold text-[#14151a]">No nutritionists match this search</p>
          </div>
        ) : (
          visibleRows.map((row) => {
            const usage = getAIUsage(row.aiUsage);
            return (
              <div
                key={`ai-${row.nutritionistId}`}
                className="border-t border-[#eef0f3] px-5 py-4 transition-colors first:border-t-0 hover:bg-[#fafbfc]"
              >
                <div className="grid gap-4 xl:grid-cols-[minmax(20rem,32rem)_minmax(0,1fr)] xl:items-center xl:gap-8">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${avatarTone(row.nutritionistId)}`}>
                        {getInitials(row.nutritionistName)}
                      </span>
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-sm font-semibold text-[#14151a]">{row.nutritionistName}</p>
                          <span title={row.classificationReason} className="shrink-0 rounded-full bg-[#f1f2f4] px-2 py-0.5 text-[10px] font-semibold text-[#61656d]">
                            {ACCOUNT_CLASSIFICATION_LABELS[row.accountClassification]}
                          </span>
                        </div>
                        <p className="truncate text-xs text-[#8a909a]">{row.nutritionistEmail || row.nutritionistId}</p>
                        <p className="truncate text-xs text-[#a3a9b2]">{formatCountry(row.country)}</p>
                      </div>
                    </div>
                    {usage.quota.actions.length > 0 ? (
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#9aa1ab]">Quota</span>
                        <AIQuotaChips usage={usage} />
                      </div>
                    ) : null}
                  </div>
                  <div className="grid min-w-0 gap-x-5 gap-y-3 sm:grid-cols-2 xl:grid-cols-[minmax(4.5rem,0.75fr)_minmax(5rem,0.75fr)_minmax(3.5rem,0.55fr)_minmax(8.5rem,1fr)_auto] xl:items-center">
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#9aa1ab]">Tokens</p>
                      <p className="text-sm font-semibold text-[#14151a]">{formatCompactNumber(usage.totalTokens)}</p>
                      <p className="text-[11px] text-[#a3a9b2]">{formatCompactNumber(usage.reasoningTokens)} reasoning</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#9aa1ab]">Spend</p>
                      <p className="text-sm font-semibold text-[#14151a]">{formatUsd(usage.estimatedCostUsd, usage.hasUnknownCost)}</p>
                      {usage.hasUnknownCost ? <p className="text-[11px] text-[#b46b18]">partial</p> : null}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#9aa1ab]">Calls</p>
                      <p className="text-sm font-semibold text-[#14151a]">{formatNumber(usage.calls)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#9aa1ab]">Next reset</p>
                      <p className="text-sm font-medium text-[#41545e]">{formatDateTime(usage.quota.nextResetAt)}</p>
                      <p className="text-[11px] text-[#a3a9b2]">{formatNumber(usage.quota.totalRemaining)} of {formatNumber(usage.quota.totalLimit)} left</p>
                    </div>
                    <div className="hidden justify-self-end sm:block">
                      <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.05em] text-[#9aa1ab]">Trend</p>
                      <MiniAIUsageBars daily={usage.daily} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const SignalPill: React.FC<{ active: boolean; label: string }> = ({ active, label }) => (
  <span
    className={
      active
        ? 'inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-[#eef6f8] px-2 py-0.5 text-xs font-medium text-[#0e7fa0]'
        : 'inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-[#f7f8fa] px-2 py-0.5 text-xs font-medium text-[#b6bcc4]'
    }
  >
    {active ? (
      <CheckCircle2 className="h-3 w-3 text-[#0e97be]" />
    ) : (
      <span className="h-1.5 w-1.5 rounded-full border border-[#cfd5db]" />
    )}
    {label}
  </span>
);

const SourceInlineItems: React.FC<{ counts: Record<string, number> }> = ({ counts }) => {
  const entries = Object.entries(counts)
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (entries.length === 0) {
    return <span className="text-xs text-[#a3a9b2]">No source activity</span>;
  }

  return (
    <>
      {entries.map(([source, count]) => (
        <span
          key={source}
          className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md bg-[#f1f2f4] px-2 py-0.5 text-xs font-medium text-[#41545e]"
        >
          <span className="truncate">{formatSource(source)}</span>
          <span className="font-semibold tabular-nums text-[#0e7fa0]">{formatNumber(count)}</span>
        </span>
      ))}
    </>
  );
};

const ActivityInlineGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-w-0 items-baseline gap-2.5">
    <p className="w-[3.75rem] shrink-0 text-[10px] font-semibold uppercase tracking-[0.07em] text-[#a0a7b0]">{label}</p>
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
  </div>
);

const getPractitionerActionTotal = (row: AdminUsageQualityPractitioner): number =>
  row.activityCount ?? (row.clientsOnboarded + row.mealPlansGenerated + row.finalizedMealPlans);

const PractitionerActivityMetric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#a0a7b0]">{label}</p>
    <p className="mt-0.5 text-sm font-semibold tabular-nums text-[#14151a]">{value}</p>
  </div>
);

type PractitionerClientOnboarding = AdminUsageQualityPractitioner['clientOnboardings'][number];

const ClientDetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg border border-[#e8eaee] bg-white px-3 py-2.5">
    <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#a0a7b0]">{label}</p>
    <div className="mt-1 break-words text-sm font-medium text-[#27333a]">{value || '-'}</div>
  </div>
);

const PractitionerClientDetailDialog: React.FC<{
  client: PractitionerClientOnboarding | null;
  practitionerName: string;
  onOpenChange: (open: boolean) => void;
}> = ({ client, practitionerName, onOpenChange }) => (
  <Dialog open={Boolean(client)} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl border-[#e8eaee] p-0">
      <DialogHeader className="border-b border-[#e8eaee] bg-gradient-to-b from-white to-[#fafbfc] px-6 py-5 text-left">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#eaf7f1] text-[#177353]">
            <ContactRound className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <DialogTitle className="truncate font-heading text-xl font-bold text-[#14151a]">
              {client?.clientLabel || 'Client activity'}
            </DialogTitle>
            <DialogDescription className="mt-1">
              Client onboarded by {practitionerName}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>
      {client ? (
        <div className="space-y-4 px-6 py-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="border-0 bg-[#eaf7f1] text-[#177353] hover:bg-[#eaf7f1]">
              {client.source === 'created' ? 'Created client' : 'Assigned client'}
            </Badge>
            <Badge variant="outline" className="capitalize text-[#41545e]">
              {client.status || 'Status unavailable'}
            </Badge>
            <span className="text-xs text-[#8a909a]">{formatDateTime(client.occurredAt)}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ClientDetailItem label="Account created" value={formatDateTime(client.clientCreatedAt)} />
            <ClientDetailItem label="Onboarding event" value={client.source === 'created' ? 'Client created' : 'Client assigned'} />
            <ClientDetailItem label="Account status" value={client.status || 'Status unavailable'} />
            <ClientDetailItem label="Activity recorded" value={formatDateTime(client.occurredAt)} />
          </div>
          <p className="text-xs leading-relaxed text-[#8a909a]">Personal details are hidden in this operational dashboard.</p>
        </div>
      ) : null}
    </DialogContent>
  </Dialog>
);

const PractitionerClientOnboardings: React.FC<{
  row: AdminUsageQualityPractitioner;
  onSelect: (client: PractitionerClientOnboarding) => void;
}> = ({ row, onSelect }) => {
  if (!row.clientOnboardings?.length) {
    return <p className="mt-2 text-[11px] text-[#a3a9b2]">No clients added in this period</p>;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {row.clientOnboardings.map((client) => (
        <button
          type="button"
          key={`${client.clientId}-${client.source}`}
          title={`${client.clientLabel} · ${client.source === 'created' ? 'Created' : 'Assigned'} ${formatDateTime(client.occurredAt)}`}
          aria-label={`View activity for ${client.clientLabel}`}
          onClick={() => onSelect(client)}
          className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#eaf7f1] px-2 py-1 text-[11px] font-medium text-[#177353] transition-colors hover:bg-[#d9f0e7] focus:outline-none focus:ring-2 focus:ring-[#0e97be]/30"
        >
          <span className="truncate">{client.clientLabel}</span>
          <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.04em] text-[#5f8d7d]">
            {client.source}
          </span>
          <Eye className="h-3 w-3 shrink-0" />
        </button>
      ))}
    </div>
  );
};

const PractitionerRow: React.FC<{
  row: AdminUsageQualityPractitioner;
  onViewMealPlans: (row: AdminUsageQualityPractitioner) => void;
}> = ({ row, onViewMealPlans }) => {
  const total = getPractitionerActionTotal(row);
  const lastSeenAt = [row.lastActivityAt, row.lastLoginAt, row.activities?.[0]?.occurredAt]
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => b.localeCompare(a))[0] || null;
  const [selectedClient, setSelectedClient] = useState<PractitionerClientOnboarding | null>(null);
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  return (
    <div className="grid gap-3 border-t border-[#eef0f3] px-4 py-3 transition-colors first:border-t-0 hover:bg-[#fafbfc] xl:grid-cols-[minmax(16rem,1.1fr)_minmax(20rem,1.3fr)_minmax(15rem,0.8fr)_minmax(9rem,0.55fr)] xl:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${avatarTone(row.nutritionistId)}`}>
          {getInitials(row.nutritionistName)}
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onViewMealPlans(row)}
              className="truncate text-left text-sm font-semibold text-[#0e7fa0] hover:text-[#096985] hover:underline"
              title={`View meal plans for ${row.nutritionistName}`}
            >
              {row.nutritionistName}
            </button>
            <span
              title={row.classificationReason}
              className={
                row.accountClassification === 'external'
                  ? 'rounded-full bg-[#eaf7f1] px-2 py-0.5 text-[10px] font-semibold text-[#177353]'
                  : row.accountClassification === 'internal'
                    ? 'rounded-full bg-[#eef0ff] px-2 py-0.5 text-[10px] font-semibold text-[#5a51c8]'
                    : 'rounded-full bg-[#fff3e7] px-2 py-0.5 text-[10px] font-semibold text-[#a85f12]'
              }
            >
              {ACCOUNT_CLASSIFICATION_LABELS[row.accountClassification]}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#41545e]">
              <span className={`h-1.5 w-1.5 rounded-full ${row.meaningfullyActive ? 'bg-[#0e97be]' : 'bg-[#c3ced4]'}`} />
              {row.meaningfullyActive ? 'Meaningfully active' : row.activated ? 'Activated' : 'Not activated'}
            </span>
          </div>
          <p className="truncate text-xs text-[#8a909a]">{row.nutritionistEmail || row.nutritionistId}</p>
          <p className="truncate text-xs text-[#a3a9b2]">
            {[row.practiceName, formatCountry(row.country)].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      <div className="grid min-w-0 gap-2">
        <ActivityInlineGroup label="Signals">
          <SignalPill active={row.activitySignals.appUsage} label="Usage" />
          <SignalPill active={row.activitySignals.lastLogin} label="Login" />
          <SignalPill active={row.activitySignals.clientOnboarding} label="Clients" />
          <SignalPill active={row.activitySignals.mealPlanGeneration} label="Plans" />
          <SignalPill active={row.activitySignals.finalizedMealPlan} label="Finalized" />
        </ActivityInlineGroup>
        <ActivityInlineGroup label="Sources">
          <SourceInlineItems counts={row.sourceCounts} />
        </ActivityInlineGroup>
      </div>

      <div className="grid grid-cols-4 gap-3 rounded-md bg-[#f7f8fa] px-3 py-2 xl:bg-transparent xl:px-0 xl:py-0">
        <PractitionerActivityMetric label="Clients" value={formatNumber(row.clientsOnboarded)} />
        <PractitionerActivityMetric label="Generated" value={formatNumber(row.mealPlansGenerated)} />
        <PractitionerActivityMetric label="Finalized" value={formatNumber(row.finalizedMealPlans)} />
        <PractitionerActivityMetric label="Smart edits" value={formatNumber(row.aiActionCounts?.smart_edit || 0)} />
        <div className="col-span-4">
          <PractitionerClientOnboardings row={row} onSelect={setSelectedClient} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 xl:flex-col xl:items-end">
        <p className="text-xs text-[#8a909a] xl:text-right">
          <span className="font-medium text-[#41545e]">{formatDate(lastSeenAt)}</span>
          <span className="mx-1 text-[#c3ced4]">·</span>
          {formatNumber(total)} activities
        </p>
        <button type="button" onClick={() => setActivitiesOpen((open) => !open)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0e7fa0] hover:text-[#096985]" aria-expanded={activitiesOpen}>
          {activitiesOpen ? 'Hide activities' : 'View activities'}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${activitiesOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {activitiesOpen ? (
        <div className="rounded-lg border border-[#e8edf0] bg-[#fbfcfd] p-3 xl:col-span-4 xl:ml-12">
          {row.activities?.length ? (
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {row.activities.map((activity) => (
                <div key={activity.id} className="flex gap-3 rounded-md px-2 py-2 hover:bg-white">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0e97be]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-[#27343b]">{activity.label}</p>
                      <time className="text-xs text-[#8a909a]">{formatDateTime(activity.occurredAt)}</time>
                    </div>
                    <p className="mt-0.5 text-xs text-[#6f7780]">{[activity.detail, formatSource(activity.source), formatSource(activity.outcome)].filter(Boolean).join(' · ')}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#8a909a]">No detailed backend activity events were recorded in this period.</p>}
        </div>
      ) : null}
      <PractitionerClientDetailDialog
        client={selectedClient}
        practitionerName={row.nutritionistName}
        onOpenChange={(open) => {
          if (!open) setSelectedClient(null);
        }}
      />
    </div>
  );
};

const PractitionerActivityList: React.FC<{
  rows: AdminUsageQualityPractitioner[];
  isInitialLoading: boolean;
  onViewMealPlans: (row: AdminUsageQualityPractitioner) => void;
}> = ({ rows, isInitialLoading, onViewMealPlans }) => {
  const visibleRows = rows;

  if (isInitialLoading) {
    return (
      <div className="divide-y divide-[#eef0f3]">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={`practitioner-skeleton-${index}`} className="flex items-center gap-4 px-5 py-4">
            <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-[#eef0f3]" />
            <div className="h-8 flex-1 animate-pulse rounded-lg bg-[#eef0f3]" />
            <div className="hidden h-8 w-48 animate-pulse rounded-lg bg-[#f2f7f9] sm:block" />
          </div>
        ))}
      </div>
    );
  }

  if (visibleRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0e97be]">
          <UsersRound className="h-6 w-6" />
        </span>
        <div>
          <p className="text-sm font-semibold text-[#14151a]">No practitioner activity found</p>
          <p className="mt-1 text-sm text-[#8a909a]">Try adjusting the date range or clearing your search.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-h-[34rem] overflow-y-auto">
        <div className="sticky top-0 z-10 hidden grid-cols-[minmax(16rem,1.1fr)_minmax(20rem,1.3fr)_minmax(15rem,0.8fr)_minmax(9rem,0.55fr)] border-b border-[#eef0f3] bg-white/95 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.07em] text-[#14151a] backdrop-blur xl:grid">
          <span>Practitioner</span>
          <span>Signals and source</span>
          <span>Weekly counts</span>
          <span className="text-right">Last seen</span>
        </div>
        {visibleRows.map((row) => (
          <PractitionerRow
            key={row.nutritionistId}
            row={row}
            onViewMealPlans={onViewMealPlans}
          />
        ))}
      </div>
    </div>
  );
};

const NutritionStrip: React.FC<{ nutrition: AdminUsageQualityRecipeNutrition | undefined | null; compact?: boolean }> = ({
  nutrition,
  compact = false,
}) => {
  const items: Array<{ label: string; value: string }> = [
    { label: 'Energy', value: formatCalories(getNutritionValue(nutrition, 'calories')) },
    { label: 'Protein', value: formatMacro(getNutritionValue(nutrition, 'protein')) },
    { label: 'Carbs', value: formatMacro(getNutritionValue(nutrition, 'carbs')) },
    { label: 'Fat', value: formatMacro(getNutritionValue(nutrition, 'fat')) },
    ...(compact ? [] : [{ label: 'Fibre', value: formatMacro(getNutritionValue(nutrition, 'fibre')) }]),
  ];
  return (
    <div className={`grid gap-px overflow-hidden rounded-xl bg-[#eef0f3] ${compact ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-5'}`}>
      {items.map((item) => (
        <div key={item.label} className="bg-[#f7f8fa] px-3.5 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-[#9aa1ab]">{item.label}</p>
          <p className="mt-1 text-sm font-semibold tabular-nums text-[#14151a]">{item.value}</p>
        </div>
      ))}
    </div>
  );
};

const SamplePlanCard: React.FC<{
  sample: AdminUsageQualitySampleMealPlan;
  selected: boolean;
  onReview: (sample: AdminUsageQualitySampleMealPlan) => void;
}> = ({ sample, selected, onReview }) => {
  const tags = [
    ...compactTags(sample.medicalConditionsApplied, 3),
    ...compactTags(sample.restrictions.allergies, 2),
    ...compactTags(sample.restrictions.intolerances, 2),
    ...compactTags(sample.restrictions.dietaryPreferences, 2),
  ];
  const averages = getSampleDailyAverages(sample);
  const activeFlags = qualityFlagsForSample(sample).filter((flag) => flag.value > 0);

  return (
    <article
      className={
        selected
          ? 'rounded-2xl border border-[#0e97be] bg-[#f6fdff] p-5 ring-1 ring-[#0e97be]/25 transition-all duration-200'
          : 'rounded-2xl border border-[#e8eaee] bg-white p-5 shadow-[0_1px_2px_rgba(17,24,39,0.04)] transition-all duration-200 hover:border-[#d5d8de] hover:shadow-[0_10px_34px_-14px_rgba(17,24,39,0.2)]'
      }
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="rounded-md border-transparent bg-[#eef6f8] text-[#0e7fa0]">
              {formatSource(sample.source)}
            </Badge>
            {sample.aiModel ? (
              <Badge variant="outline" className="rounded-md border-transparent bg-[#f2f2ff] text-[#5a51c8]">
                {sample.aiModel}
              </Badge>
            ) : null}
            {sample.status ? (
              <Badge variant="outline" className="rounded-md border-transparent bg-[#f1f2f4] text-[#5a6b74]">
                {sample.status}
              </Badge>
            ) : null}
            {sample.generationState === 'failed' ? (
              <Badge variant="outline" className="rounded-md border-[#ffd7d2] bg-[#fff6f5] text-[#b42318]">
                Generation failed
              </Badge>
            ) : null}
          </div>
          <h3 className="mt-2.5 font-heading text-[17px] font-bold leading-snug tracking-tight text-[#14151a]">{sample.name}</h3>
          <p className="mt-1 text-[13px] text-[#8a909a]">
            {sample.nutritionistName} · {formatCountry(sample.nutritionistCountry)} · {sample.clientName} · {formatDateTime(sample.generatedAt || sample.createdAt)}
          </p>
        </div>
        <Button type="button" className="shrink-0 gap-1.5 bg-[#14151a] text-white transition-colors hover:bg-[#0e97be]" onClick={() => onReview(sample)}>
          <Eye className="h-4 w-4" />
          {sample.mealCount > 0 ? 'Review' : 'Inspect'}
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-[#eef0f3] sm:grid-cols-4">
        {[
          { label: 'Days', value: sample.days ?? '-' },
          { label: 'Meals', value: formatNumber(sample.mealCount) },
          { label: 'Target', value: sample.targetCalories ? formatCalories(sample.targetCalories) : '-' },
          { label: 'Avg protein', value: formatMacro(averages.protein) },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#f7f8fa] px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-[#9aa1ab]">{stat.label}</p>
            <p className="mt-1 text-sm font-semibold tabular-nums text-[#14151a]">{stat.value}</p>
          </div>
        ))}
      </div>

      {tags.length > 0 || activeFlags.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <Badge key={tag} variant="outline" className="border-transparent bg-[#eafaf3] text-[#0f7d57]">
              {tag}
            </Badge>
          ))}
          {activeFlags.map((flag) => (
            <span key={flag.label} className={`rounded-md border px-2 py-0.5 text-xs font-medium ${flag.tone}`}>
              {flag.label}: {formatNumber(flag.value)}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-4 divide-y divide-[#eef0f3] rounded-xl bg-[#fafbfc]">
        {sample.mealPreview.length === 0 ? (
          <div className="px-4 py-5 text-sm text-[#8a909a]">No meal preview available.</div>
        ) : (
          sample.mealPreview.slice(0, 4).map((meal, index) => {
            const hasSelectedRecipe = hasMealPreviewSelection(meal);
            const suggestions = hasSelectedRecipe ? [] : getMealPreviewSuggestions(sample, meal);
            const firstSuggestion = suggestions[0];
            const additionalSuggestionCount = Math.max(0, suggestions.length - 1);
            return (
              <div
                key={`${sample.draftId}-${meal.dayNumber}-${meal.mealName}-${index}`}
                className="grid gap-1 px-4 py-2.5 transition-colors hover:bg-white md:grid-cols-[7rem_minmax(10rem,1fr)_auto] md:items-center md:gap-3"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#9aa1ab]">
                  Day {meal.dayNumber} · {meal.mealName}
                </p>
                <div className="min-w-0">
                  <p className={`truncate text-sm font-medium ${firstSuggestion || hasSelectedRecipe ? 'text-[#14151a]' : 'text-[#b42318]'}`}>
                    {hasSelectedRecipe ? meal.recipeTitle : firstSuggestion?.title || 'No AI meal suggestions captured'}
                  </p>
                  {!hasSelectedRecipe && additionalSuggestionCount > 0 ? (
                    <p className="mt-0.5 text-xs text-[#8a909a]">+{additionalSuggestionCount} more suggested meal{additionalSuggestionCount === 1 ? '' : 's'}</p>
                  ) : null}
                </div>
                <div className="md:text-right">
                  <p className="text-[13px] tabular-nums text-[#8a909a]">
                    {hasSelectedRecipe
                      ? `${formatCalories(meal.calories)} · ${formatMacro(meal.protein)} P`
                      : firstSuggestion
                        ? `${formatCalories(firstSuggestion.calories)} · ${formatMacro(firstSuggestion.protein)} P`
                        : 'No nutrition captured'}
                  </p>
                  {!hasSelectedRecipe ? <p className="mt-0.5 text-xs font-medium text-[#b54708]">Not selected</p> : null}
                </div>
              </div>
            );
          })
        )}
      </div>
    </article>
  );
};

const ReviewRecipeBlock: React.FC<{ recipe: AdminUsageQualityRecipeDetail | null }> = ({ recipe }) => {
  if (!recipe) {
    return (
      <div className="rounded-lg border border-[#ffd7d2] bg-[#fff6f5] px-4 py-3 text-sm text-[#b42318]">
        No selected recipe captured for this meal.
      </div>
    );
  }

  const badges = [
    ...recipe.allergens.map((value) => ({ value, tone: 'border-[#ffd7d2] bg-[#fff6f5] text-[#b42318]' })),
    ...recipe.intoleranceTags.map((value) => ({ value, tone: 'border-[#f8dfb6] bg-[#fff8eb] text-[#8a4b00]' })),
    ...recipe.dietLabels.slice(0, 4).map((value) => ({ value, tone: 'border-[#c7ecd8] bg-[#eafaf3] text-[#0f7d57]' })),
    ...(recipe.customized ? [{ value: 'Customized', tone: 'border-[#d9d4f0] bg-[#f7f5ff] text-[#514a8a]' }] : []),
  ];

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-base font-semibold text-[#14151a]">{recipe.title}</h4>
          {recipe.generatedVia ? (
            <Badge variant="outline" className="border-[#d9d4f0] bg-[#f7f5ff] text-[#514a8a]">
              {recipe.generatedVia}
            </Badge>
          ) : null}
          {recipe.readyInMinutes ? (
            <Badge variant="outline" className="border-[#eef0f3] bg-white text-[#5a6b74]">
              {recipe.readyInMinutes} min
            </Badge>
          ) : null}
        </div>
        {recipe.description ? <p className="mt-2 text-sm leading-6 text-[#5a6b74]">{recipe.description}</p> : null}
        {badges.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {badges.map((badge) => (
              <span key={`${recipe.title}-${badge.value}`} className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${badge.tone}`}>
                {badge.value}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <NutritionStrip nutrition={recipe.nutrition} />

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl bg-[#f7f8fa] p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#9aa1ab]">Ingredients</p>
          {recipe.ingredients.length > 0 ? (
            <ul className="mt-3 space-y-2 text-sm text-[#41545e]">
              {recipe.ingredients.map((ingredient, index) => (
                <li key={`${recipe.title}-ingredient-${index}`} className="flex gap-2 leading-5">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#c3ced4]" />
                  {ingredient}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-[#8a909a]">No ingredients captured.</p>
          )}
        </div>
        <div className="rounded-xl bg-[#f7f8fa] p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#9aa1ab]">Instructions</p>
          {recipe.instructions.length > 0 ? (
            <ol className="mt-3 space-y-2.5 text-sm text-[#41545e]">
              {recipe.instructions.map((instruction, index) => (
                <li key={`${recipe.title}-instruction-${index}`} className="flex gap-2.5 leading-5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold tabular-nums text-[#0e97be]">{index + 1}</span>
                  <span className="pt-px">{instruction}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-3 text-sm text-[#8a909a]">No instructions captured.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const MealDetailCard: React.FC<{ meal: AdminUsageQualityMealDetail }> = ({ meal }) => {
  const hasSelectedRecipe = Boolean(meal.selectedRecipe);
  const suggestionLabel = hasSelectedRecipe ? 'alternate' : 'suggestion';

  return (
    <div className="rounded-2xl border border-[#eef0f3] bg-white p-5">
      <div className="flex flex-col gap-3 border-b border-[#eef0f3] pb-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-[15px] font-bold tracking-tight text-[#14151a]">{meal.mealName}</h3>
            {meal.mealTime ? (
              <Badge variant="outline" className="rounded-md border-transparent bg-[#f1f2f4] text-[#5a6b74]">
                {meal.mealTime}
              </Badge>
            ) : null}
            {meal.targetCalories ? (
              <Badge variant="outline" className="rounded-md border-transparent bg-[#f1f2f4] text-[#5a6b74]">
                Target {formatCalories(meal.targetCalories)}
              </Badge>
            ) : null}
          </div>
          {meal.notes ? <p className="mt-2 text-sm text-[#5a6b74]">{meal.notes}</p> : null}
        </div>
        <span className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-[#f7f8fa] px-3 py-1.5 text-xs font-medium text-[#5a6b74]">
          <Utensils className="h-3.5 w-3.5" />
          {meal.alternatives.length} {suggestionLabel}{meal.alternatives.length === 1 ? '' : 's'}
        </span>
      </div>
      {hasSelectedRecipe ? (
        <div className="mt-4">
          <ReviewRecipeBlock recipe={meal.selectedRecipe} />
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-[#f8dfb6] bg-[#fff8eb] px-4 py-3 text-sm text-[#8a4b00]">
          No meal was selected. The AI-generated meal suggestions are shown below for review.
        </div>
      )}
      {meal.alternatives.length > 0 ? (
        <div className="mt-4 rounded-xl bg-[#f7f8fa] p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#9aa1ab]">
            {hasSelectedRecipe ? 'Alternates' : 'AI-generated meal suggestions'}
          </p>
          <div className="mt-3 space-y-2">
            {meal.alternatives.map((recipe) => (
              <details key={`${meal.mealName}-${recipe.recipeId}-${recipe.title}`} className="group rounded-lg bg-white shadow-[0_1px_2px_rgba(17,24,39,0.04)]">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 marker:hidden">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#14151a]">{recipe.title}</p>
                    <p className="mt-0.5 text-xs tabular-nums text-[#8a909a]">
                      {formatCalories(recipe.nutrition.calories)} · {formatMacro(recipe.nutrition.protein)} protein
                    </p>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[#0e7fa0]">
                    Full recipe
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                  </span>
                </summary>
                <div className="border-t border-[#eef0f3] p-4">
                  <ReviewRecipeBlock recipe={recipe} />
                </div>
              </details>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const MealPlanDetailDialog: React.FC<{
  sample: AdminUsageQualitySampleMealPlan | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ sample, open, onOpenChange }) => {
  const days = sample ? getSampleDetailDays(sample) : [];
  const averages = sample ? getSampleDailyAverages(sample) : null;
  const flags = sample ? qualityFlagsForSample(sample) : [];
  const defaultTab = days[0] ? String(days[0].dayNumber) : 'summary';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1rem)] max-w-[1180px] flex-col gap-0 overflow-hidden rounded-2xl border border-[#e8eaee] bg-[#fafbfc] p-0 sm:w-[calc(100vw-2rem)]">
        <DialogHeader className="border-b border-[#e8eaee] bg-gradient-to-b from-white to-[#fafbfc] px-4 py-5 sm:px-6">
          <div className="flex flex-col gap-4 pr-8 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <DialogTitle className="font-heading text-xl font-bold leading-tight tracking-tight text-[#14151a] sm:text-2xl">{sample?.name || 'Meal plan detail'}</DialogTitle>
              <DialogDescription className="mt-2 text-sm text-[#5a6b74]">
                {sample ? `${sample.clientName} · ${sample.nutritionistName} · ${formatCountry(sample.nutritionistCountry)} · ${formatDateTime(sample.generatedAt || sample.createdAt)}` : ''}
              </DialogDescription>
            </div>
            {sample ? (
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="rounded-md border-[#e0e2e7] bg-[#f7f8fa] text-[#41545e]">
                  {formatSource(sample.source)}
                </Badge>
                {sample.aiModel ? (
                  <Badge variant="outline" className="rounded-md border-[#d9d4f0] bg-[#f7f5ff] text-[#514a8a]">
                    {sample.aiModel}
                  </Badge>
                ) : null}
                {sample.generationState ? (
                  <Badge
                    variant="outline"
                    className={sample.generationState === 'failed'
                      ? 'rounded-md border-[#ffd7d2] bg-[#fff6f5] text-[#b42318]'
                      : 'rounded-md border-[#eef0f3] bg-white text-[#5a6b74]'}
                  >
                    {sample.generationState === 'failed' ? 'Generation failed' : formatSource(sample.generationState)}
                  </Badge>
                ) : null}
                <Badge variant="outline" className="rounded-md border-[#eef0f3] bg-white text-[#5a6b74]">
                  {sample.mealCount} meals
                </Badge>
              </div>
            ) : null}
          </div>
        </DialogHeader>

        {sample && days.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 sm:px-6">
            <div className="mx-auto max-w-2xl rounded-2xl border border-[#f8dfb6] bg-[#fff8eb] p-6 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#b46b18]">
                <AlertTriangle className="h-6 w-6" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#14151a]">
                {sample.generationState === 'failed' ? 'Meal-plan generation failed' : 'No meal data was saved'}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6f5a37]">
                {sample.generationState === 'failed'
                  ? 'The draft shell and client context were created, but generation stopped before any meal suggestions were saved. It is retained for diagnostics and has no recipes to review.'
                  : 'This draft contains no days, selected meals, or recipe suggestions, so a detailed quality review is not available.'}
              </p>
            </div>
          </div>
        ) : sample ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-[#eef0f3] bg-white p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[#0e97be]" />
                    <h3 className="text-sm font-semibold text-[#14151a]">Daily average</h3>
                  </div>
                  <NutritionStrip nutrition={averages} />
                </div>

                <Tabs defaultValue={defaultTab} className="w-full">
                  <div className="overflow-x-auto pb-1">
                    <TabsList className="h-auto justify-start gap-1 rounded-xl bg-[#eef0f3] p-1">
                      {days.map((day) => (
                        <TabsTrigger key={day.dayNumber} value={String(day.dayNumber)} className="rounded-lg px-4 py-2 font-medium text-[#5a6b74] transition-all data-[state=active]:bg-[#0e97be] data-[state=active]:text-white data-[state=active]:shadow-sm">
                          Day {day.dayNumber}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  {days.map((day) => (
                    <TabsContent key={day.dayNumber} value={String(day.dayNumber)} className="mt-4 space-y-4">
                      <div className="rounded-2xl border border-[#eef0f3] bg-white p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-base font-semibold text-[#14151a]">Day {day.dayNumber}</h3>
                            <p className="mt-1 text-sm text-[#5a6b74]">{formatDate(day.date)}</p>
                          </div>
                          <NutritionStrip nutrition={day.totals} compact />
                        </div>
                        {day.notes ? <p className="mt-3 text-sm text-[#5a6b74]">{day.notes}</p> : null}
                      </div>
                      {day.meals.map((meal) => (
                        <MealDetailCard key={`${day.dayNumber}-${meal.mealOrder}-${meal.mealName}`} meal={meal} />
                      ))}
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              <aside className="space-y-4 self-start xl:sticky xl:top-0">
                <div className="rounded-2xl border border-[#eef0f3] bg-white p-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[#0f9b6c]" />
                    <h3 className="text-sm font-semibold text-[#14151a]">Applied context</h3>
                  </div>
                  <div className="mt-4 space-y-3">
                    <ContextTagBlock label="Conditions" values={sample.medicalConditionsApplied} />
                    <ContextTagBlock label="Allergies" values={sample.restrictions.allergies} />
                    <ContextTagBlock label="Intolerances" values={sample.restrictions.intolerances} />
                    <ContextTagBlock label="Cuisines" values={sample.restrictions.cuisines} />
                    <ContextTagBlock label="Preferences" values={sample.restrictions.dietaryPreferences} />
                  </div>
                </div>

                <div className="rounded-2xl border border-[#eef0f3] bg-white p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-[#b46b18]" />
                    <h3 className="text-sm font-semibold text-[#14151a]">Review signals</h3>
                  </div>
                  <div className="mt-4 space-y-2">
                    {flags.length > 0 ? (
                      flags.map((flag) => (
                        <div key={flag.label} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${flag.tone}`}>
                          <span className="text-sm">{flag.label}</span>
                          <span className="text-sm font-semibold">{formatNumber(flag.value)}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-[#8a909a]">No structured review signals captured.</p>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const ContextTagBlock: React.FC<{ label: string; values: string[] }> = ({ label, values }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#8a909a]">{label}</p>
    {values.length > 0 ? (
      <div className="mt-2 flex flex-wrap gap-2">
        {values.map((value) => (
          <Badge key={`${label}-${value}`} variant="outline" className="border-[#eef0f3] bg-[#f7f8fa] text-[#41545e]">
            {value}
          </Badge>
        ))}
      </div>
    ) : (
      <p className="mt-1 text-sm text-[#a3a9b2]">None</p>
    )}
  </div>
);

const CohortColumn: React.FC<{ group: AdminUsageQualityCohortGroup }> = ({ group }) => (
  <section className="flex flex-col overflow-hidden rounded-2xl border border-[#e8eaee] bg-white shadow-[0_1px_2px_rgba(17,24,39,0.04)] transition-shadow duration-200 hover:shadow-[0_10px_30px_-16px_rgba(17,24,39,0.2)]">
    <div className="flex items-center justify-between gap-3 border-b border-[#eef0f3] px-5 py-4">
      <div className="min-w-0">
        <h3 className="truncate font-heading text-[15px] font-bold tracking-tight text-[#14151a]">{getAIMealPlanCohortLabel(group.cohort)}</h3>
        <p className="mt-0.5 text-xs text-[#8a909a]">{formatNumber(group.count)} member{group.count === 1 ? '' : 's'}</p>
      </div>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f2f2ff] text-[#5877c7]">
        <BrainCircuit className="h-[18px] w-[18px]" />
      </span>
    </div>
    <div className="max-h-96 divide-y divide-[#eef0f3] overflow-y-auto">
      {group.members.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-[#8a909a]">No members.</div>
      ) : (
        group.members.map((member) => (
          <div key={`${group.cohort}-${member.email}`} className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-[#fafbfc]">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${avatarTone(member.email)}`}>
              {getInitials(member.displayName || member.email)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#14151a]">{member.displayName || member.email}</p>
              <p className="truncate text-xs text-[#8a909a]">{member.email}</p>
              <p className="truncate text-xs text-[#a3a9b2]">
                {member.status === 'pre_provisioned' && !member.country ? 'Country not available' : formatCountry(member.country)}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Badge
                variant="outline"
                className={
                  member.status === 'pre_provisioned'
                    ? 'rounded-full border-transparent bg-[#f2f2ff] text-[#5a51c8]'
                    : member.status === 'disabled'
                      ? 'rounded-full border-transparent bg-[#fdecea] text-[#b42318]'
                      : 'rounded-full border-transparent bg-[#eafaf3] text-[#0f9b6c]'
                }
              >
                {member.status === 'pre_provisioned' ? 'Pending' : member.status}
              </Badge>
              <span className="text-[11px] text-[#a3a9b2]">{formatDate(member.lastLoginAt)}</span>
            </div>
          </div>
        ))
      )}
    </div>
  </section>
);

const UsageQualityMetricsPage: React.FC = () => {
  const [fromDate, setFromDate] = useState(getDefaultFromDate);
  const [toDate, setToDate] = useState(getTodayDate);
  const [sampleLimit, setSampleLimit] = useState<string>(String(SAMPLE_LIMIT_OPTIONS[0]));
  const [data, setData] = useState<AdminUsageQualityWeeklyResponse['data'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [practitionerQuery, setPractitionerQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState<PractitionerAccountFilter>('external');
  const [practitionerRecencyFilter, setPractitionerRecencyFilter] = useState<PractitionerRecencyFilter>('7');
  const [practitionerStatusFilter, setPractitionerStatusFilter] = useState<PractitionerStatusFilter>('all');
  const [sampleQuery, setSampleQuery] = useState('');
  const [debouncedSampleQuery, setDebouncedSampleQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [samplePractitioner, setSamplePractitioner] = useState<Pick<AdminUsageQualityPractitioner, 'nutritionistId' | 'nutritionistName'> | null>(null);
  const [selectedSample, setSelectedSample] = useState<AdminUsageQualitySampleMealPlan | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<UsageQualityTab>('ai');
  const latestMetricsRequest = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSampleQuery(sampleQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [sampleQuery]);

  const loadMetrics = useCallback(async (signal?: AbortSignal) => {
    const requestId = latestMetricsRequest.current + 1;
    latestMetricsRequest.current = requestId;
    setIsLoading(true);
    try {
      const response = await getAdminUsageQualityWeekly({
        from: fromDate || undefined,
        to: toDate || undefined,
        sampleLimit: samplePractitioner ? PRACTITIONER_MEAL_PLAN_LIMIT : Number(sampleLimit),
        search: debouncedSampleQuery || undefined,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        nutritionistId: samplePractitioner?.nutritionistId,
      }, signal);
      if (requestId === latestMetricsRequest.current) {
        setData(response.data);
      }
    } catch (error) {
      if (axios.isCancel(error) || signal?.aborted) {
        return;
      }
      toast({
        title: 'Metrics failed to load',
        description: getErrorMessage(error, 'Unable to load usage and quality metrics right now.'),
        variant: 'destructive',
      });
    } finally {
      if (requestId === latestMetricsRequest.current) {
        setIsLoading(false);
      }
    }
  }, [debouncedSampleQuery, fromDate, sampleLimit, samplePractitioner, sourceFilter, toDate]);

  useEffect(() => {
    const controller = new AbortController();
    void loadMetrics(controller.signal);
    return () => controller.abort();
  }, [loadMetrics]);

  const summary = data?.summary;
  const generated = summary?.mealPlansGenerated || 0;
  const finalized = summary?.finalizedMealPlans || 0;
  const rate = completionRate(finalized, generated);
  const aiUsage = useMemo(() => getAIUsage(data?.aiUsage), [data?.aiUsage]);

  const sourceOptions = useMemo(() => {
    const sources = new Set<string>();
    Object.keys(data?.summary.sourceCounts || {}).forEach((source) => sources.add(source));
    data?.sampleMealPlans.forEach((sample) => sources.add(sample.source));
    return Array.from(sources).sort((a, b) => formatSource(a).localeCompare(formatSource(b)));
  }, [data?.sampleMealPlans, data?.summary.sourceCounts]);

  const filteredPractitioners = useMemo(() => {
    const query = practitionerQuery.trim().toLowerCase();
    const rows = data?.practitioners || [];
    return rows.filter((row) => {
      const matchesRecency = Boolean(query) || practitionerRecencyFilter === 'all'
        || hasPractitionerActivityWithinDays(row, Number(practitionerRecencyFilter));
      const matchesAccount = accountFilter === 'all' || row.accountClassification === accountFilter;
      const matchesStatus = practitionerStatusFilter === 'all'
        || (practitionerStatusFilter === 'genuine' && row.accountClassification === 'external' && !row.disabled)
        || (practitionerStatusFilter === 'activated' && row.activated && !row.disabled)
        || (practitionerStatusFilter === 'meaningful' && row.meaningfullyActive && !row.disabled)
        || (practitionerStatusFilter === 'inactive' && !row.meaningfullyActive);
      const matchesQuery = !query || [
        row.nutritionistName,
        row.nutritionistEmail || '',
        row.practiceName || '',
        row.country || '',
        formatCountry(row.country),
      ].some((value) => value.toLowerCase().includes(query));
      return matchesRecency && matchesAccount && matchesStatus && matchesQuery;
    });
  }, [accountFilter, data?.practitioners, practitionerQuery, practitionerRecencyFilter, practitionerStatusFilter]);

  const filteredSamples = data?.sampleMealPlans || [];

  const reviewPreviewSamples = useMemo(
    () => (data?.sampleMealPlans || []).slice(0, 2),
    [data?.sampleMealPlans],
  );

  const applyQuickWindow = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days + 1);
    setFromDate(toDateInputValue(from));
    setToDate(toDateInputValue(to));
  };

  const openPractitionerDetails = (status: PractitionerStatusFilter) => {
    setAccountFilter('external');
    setPractitionerStatusFilter(status);
    setActiveTab('activity');
    window.setTimeout(() => document.getElementById('practitioner-activity-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const openTabDetails = (tab: UsageQualityTab, elementId?: string) => {
    setActiveTab(tab);
    if (elementId) {
      window.setTimeout(() => document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    }
  };

  const viewPractitionerMealPlans = (row: AdminUsageQualityPractitioner) => {
    applyQuickWindow(90);
    setSamplePractitioner({
      nutritionistId: row.nutritionistId,
      nutritionistName: row.nutritionistName,
    });
    setSampleQuery('');
    setSourceFilter('all');
    setActiveTab('quality');
    window.setTimeout(() => document.getElementById('meal-plan-review-queue')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const openSample = (sample: AdminUsageQualitySampleMealPlan) => {
    setSelectedSample(sample);
    setDetailOpen(true);
  };

  return (
    <main className="min-h-[calc(100vh-7.5rem)] overflow-hidden rounded-[28px] border border-[#dbe6eb] bg-white px-4 py-5 sm:px-6 lg:px-8">
      <div className="w-full">
        {/* Command bar */}
        <div className="sticky top-0 z-30 border-b border-[#eceef1] bg-white/90 py-3 backdrop-blur-md">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0e97be]/10 text-[#0e97be]">
                <BarChart3 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="truncate font-heading text-lg font-bold tracking-tight text-[#14151a]">Usage &amp; Quality</h1>
                  <span className="hidden shrink-0 items-center gap-1 rounded-full border border-[#e4e6ea] bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#0e97be] sm:inline-flex">
                    <ShieldCheck className="h-3 w-3" /> Admin
                  </span>
                </div>
                <p className="truncate text-xs text-[#8a909a]">
                  {formatUsageWindowRange(data?.window)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center rounded-lg border border-[#e6e8ec] bg-white p-0.5">
                {QUICK_WINDOWS.map((window) => {
                  const active = matchesQuickWindow(fromDate, toDate, window.days);
                  return (
                    <button
                      key={window.days}
                      type="button"
                      onClick={() => applyQuickWindow(window.days)}
                      className={
                        active
                          ? 'rounded-md bg-[#0e97be] px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm'
                          : 'rounded-md px-2.5 py-1.5 text-xs font-medium text-[#61656d] transition-colors hover:text-[#14151a]'
                      }
                    >
                      {window.label}
                    </button>
                  );
                })}
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="gap-2 border-[#e0e2e7] bg-white font-medium text-[#3f434b] hover:bg-[#f6f7f9]">
                    <CalendarDays className="h-4 w-4 text-[#71767f]" />
                    <span className="hidden sm:inline">{formatDate(fromDate)} – {formatDate(toDate)}</span>
                    <span className="sm:hidden">Range</span>
                    <ChevronDown className="h-4 w-4 text-[#9aa1ab]" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[calc(100vw-2rem)] rounded-xl border-[#e8eaee] p-4 shadow-lg sm:w-[19rem]">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-[#14151a]">
                      <SlidersHorizontal className="h-4 w-4 text-[#0e97be]" />
                      <p className="text-sm font-semibold">Data settings</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block space-y-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8a909a]">From</span>
                        <Input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
                      </label>
                      <label className="block space-y-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8a909a]">To</span>
                        <Input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
                      </label>
                    </div>
                    <label className="block space-y-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8a909a]">Sample size</span>
                      <Select value={sampleLimit} onValueChange={setSampleLimit}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SAMPLE_LIMIT_OPTIONS.map((option) => (
                            <SelectItem key={option} value={String(option)}>
                              {option} samples
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                    <div className="flex flex-wrap gap-1.5 border-t border-[#eef0f3] pt-3">
                      {QUICK_WINDOWS.map((window) => (
                        <Button
                          key={window.days}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => applyQuickWindow(window.days)}
                          className={matchesQuickWindow(fromDate, toDate, window.days) ? 'border-[#0e97be] text-[#0e97be]' : 'border-[#e0e2e7]'}
                        >
                          {window.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Button type="button" onClick={() => void loadMetrics()} disabled={isLoading} className="gap-2 bg-[#0e97be] text-white shadow-sm transition-colors hover:bg-[#0c86a9]">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-6 pb-4 pt-6">
          {/* KPI band */}
          <div className="duration-500 animate-in fade-in-50">
            <StatBand>
              <StatCell
                label="Genuine practitioners"
                value={summary?.genuinePractitioners || 0}
                detail="Enabled external accounts only"
                definition="External practitioner accounts that are enabled and are not classified as internal, test/demo, or duplicate."
                actionLabel="View genuine practitioners"
                onAction={() => openPractitionerDetails('genuine')}
                icon={UsersRound}
                accent="text-[#0e97be]"
              />
              <StatCell
                label="Activated practitioners"
                value={summary?.activatedPractitioners || 0}
                detail="Genuine practitioners who have signed in or completed a meaningful action"
                definition="A genuine practitioner who has successfully signed in at least once or completed a meaningful product action."
                actionLabel="View activation activity"
                onAction={() => openPractitionerDetails('activated')}
                icon={CheckCircle2}
                accent="text-[#7c6cf0]"
              />
              <StatCell
                label="Meaningfully active"
                value={summary?.meaningfullyActivePractitioners || 0}
                detail="Created clients or used meal-plan features in this period"
                definition="A genuine practitioner who added a client, generated or finalized a meal plan, or completed a logged AI call in the selected period. Login alone does not count."
                actionLabel="View what they did"
                onAction={() => openPractitionerDetails('meaningful')}
                icon={Activity}
                accent="text-[#0f9b6c]"
              />
              <StatCell
                label="Plans finalized"
                value={finalized}
                detail={`${rate}% of ${formatNumber(generated)} generated plans`}
                definition="Meal plans completed by genuine external practitioners in the selected period. The percentage is finalized plans divided by generated plans."
                actionLabel="View practitioner activity"
                onAction={() => openPractitionerDetails('meaningful')}
                icon={CheckCircle2}
                accent="text-[#e08a1e]"
                aside={<span className="rounded-full bg-[#eafaf3] px-2 py-0.5 text-xs font-semibold text-[#0f9b6c]">{rate}%</span>}
              />
            </StatBand>
            <p className="mt-2 text-xs text-[#8a909a]">
              Default reporting excludes {formatNumber(summary?.accountClassificationCounts?.internal || 0)} internal,{' '}
              {formatNumber(summary?.accountClassificationCounts?.test_demo || 0)} test/demo, and{' '}
              {formatNumber(summary?.accountClassificationCounts?.duplicate || 0)} duplicate accounts. Use the practitioner filters to inspect them.
            </p>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as UsageQualityTab)}
            className="space-y-6"
          >
            <div className="overflow-x-auto">
              <TabsList className="h-auto w-full min-w-max justify-start gap-6 rounded-none border-b border-[#eceef1] bg-transparent p-0">
                {[
                  { value: 'ai', label: 'AI Usage', count: formatNumber(aiUsage.calls) },
                  { value: 'activity', label: 'Practitioners', count: formatNumber(data?.practitioners.length || 0) },
                  { value: 'quality', label: 'Quality Review', count: formatNumber(data?.sampleMealPlans.length || 0) },
                  { value: 'cohorts', label: 'Cohorts', count: formatNumber(data?.cohorts.length || 0) },
                ].map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="relative -mb-px flex items-center gap-2 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-1.5 text-sm font-medium text-[#71767f] shadow-none transition-colors hover:text-[#14151a] data-[state=active]:border-[#0e97be] data-[state=active]:bg-transparent data-[state=active]:text-[#14151a] data-[state=active]:shadow-none"
                  >
                    {tab.label}
                    <span className="rounded-full bg-[#f1f2f4] px-1.5 py-0.5 text-[11px] font-semibold text-[#71767f]">{tab.count}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

          <TabsContent value="ai" className="mt-0 space-y-5 duration-300 animate-in fade-in-50 slide-in-from-bottom-1">
            {reviewPreviewSamples.length > 0 ? (
              <SectionShell
                title="Review Samples"
                eyebrow="Quality cards"
                action={
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[#e0e2e7]"
                    onClick={() => setActiveTab('quality')}
                  >
                    <Eye className="h-4 w-4" />
                    View all {formatNumber(data?.sampleMealPlans.length || 0)}
                  </Button>
                }
              >
                <div className="grid gap-4 p-5 xl:grid-cols-2">
                  {reviewPreviewSamples.map((sample) => (
                    <SamplePlanCard
                      key={`preview-${sample.draftId}`}
                      sample={sample}
                      selected={selectedSample?.draftId === sample.draftId}
                      onReview={openSample}
                    />
                  ))}
                </div>
              </SectionShell>
            ) : null}

            <StatBand>
              <StatCell
                label="AI calls"
                value={aiUsage.calls}
                detail={`${formatCompactNumber(aiUsage.promptTokens)} input tokens`}
                icon={BrainCircuit}
                accent="text-[#0e97be]"
                definition="Completed AI calls made by genuine external practitioners in the selected period."
                onAction={() => openTabDetails('ai', 'per-practitioner-ai-usage')}
              />
              <StatCell
                label="AI tokens"
                value={formatCompactNumber(aiUsage.totalTokens)}
                detail={`${formatCompactNumber(aiUsage.completionTokens)} output, ${formatCompactNumber(aiUsage.reasoningTokens)} reasoning`}
                icon={BarChart3}
                accent="text-[#7c6cf0]"
                definition="Input, output, cached, and reasoning tokens recorded for genuine external practitioner activity."
                onAction={() => openTabDetails('ai', 'ai-token-spend-trend')}
              />
              <StatCell
                label="Quota left"
                value={`${formatNumber(aiUsage.quota.totalRemaining)}/${formatNumber(aiUsage.quota.totalLimit)}`}
                detail={`Next reset ${formatDateTime(aiUsage.quota.nextResetAt)}`}
                icon={Gauge}
                accent="text-[#e08a1e]"
                definition="Combined remaining rolling limits for the external practitioner accounts included by default."
                onAction={() => openTabDetails('ai', 'ai-quota-detail')}
              />
              <StatCell
                label="Clients onboarded"
                value={summary?.clientsOnboarded || 0}
                detail="New client-account or assignment signals"
                icon={UsersRound}
                accent="text-[#0f9b6c]"
                definition="External client accounts created or assigned to genuine practitioners in the selected period; internal client accounts are excluded."
                onAction={() => openPractitionerDetails('meaningful')}
              />
            </StatBand>

            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
              <SectionShell id="ai-token-spend-trend" title="Token & Spend Trend" eyebrow="Meal-plan AI usage">
                <AIUsageTrendCharts usage={aiUsage} />
              </SectionShell>
              <SectionShell id="ai-quota-detail" title="Quota Reset" eyebrow="Rolling limits">
                <AIQuotaOverview usage={aiUsage} />
              </SectionShell>
            </div>

            <SectionShell
              id="per-practitioner-ai-usage"
              title="Per-Nutritionist AI Usage"
              eyebrow="Tokens, spend, quota"
              action={
                <div className="grid w-full gap-2 sm:grid-cols-2 xl:grid-cols-[11rem_11rem_11rem_18rem]">
                  <Select value={practitionerRecencyFilter} onValueChange={(value) => setPractitionerRecencyFilter(value as PractitionerRecencyFilter)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Active last 7 days</SelectItem>
                      <SelectItem value="30">Active last 30 days</SelectItem>
                      <SelectItem value="all">All registered users</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={accountFilter} onValueChange={(value) => setAccountFilter(value as PractitionerAccountFilter)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All account types</SelectItem>
                      {Object.entries(ACCOUNT_CLASSIFICATION_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={practitionerStatusFilter} onValueChange={(value) => setPractitionerStatusFilter(value as PractitionerStatusFilter)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All activity</SelectItem>
                      <SelectItem value="genuine">Genuine</SelectItem>
                      <SelectItem value="activated">Activated</SelectItem>
                      <SelectItem value="meaningful">Meaningfully active</SelectItem>
                      <SelectItem value="inactive">No meaningful activity</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a909a]" />
                    <Input value={practitionerQuery} onChange={(event) => setPractitionerQuery(event.target.value)} placeholder="Search practitioners" className="pl-9" />
                  </div>
                </div>
              }
            >
              <AIUsageNutritionistTable rows={filteredPractitioners} />
            </SectionShell>
          </TabsContent>

          <TabsContent value="activity" className="mt-0 space-y-5 duration-300 animate-in fade-in-50 slide-in-from-bottom-1">
            <SectionShell title="Source Mix" eyebrow="Generated and finalized">
              <SourceMix counts={summary?.sourceCounts || {}} />
            </SectionShell>

            <SectionShell
              id="practitioner-activity-detail"
              title="Practitioner Activity"
              eyebrow="Weekly usage"
              action={
                <div className="grid w-full gap-2 sm:grid-cols-2 xl:grid-cols-[11rem_11rem_11rem_18rem]">
                  <Select value={practitionerRecencyFilter} onValueChange={(value) => setPractitionerRecencyFilter(value as PractitionerRecencyFilter)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Active last 7 days</SelectItem>
                      <SelectItem value="30">Active last 30 days</SelectItem>
                      <SelectItem value="all">All registered users</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={accountFilter} onValueChange={(value) => setAccountFilter(value as PractitionerAccountFilter)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All account types</SelectItem>
                      {Object.entries(ACCOUNT_CLASSIFICATION_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={practitionerStatusFilter} onValueChange={(value) => setPractitionerStatusFilter(value as PractitionerStatusFilter)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All activity</SelectItem>
                      <SelectItem value="genuine">Genuine</SelectItem>
                      <SelectItem value="activated">Activated</SelectItem>
                      <SelectItem value="meaningful">Meaningfully active</SelectItem>
                      <SelectItem value="inactive">No meaningful activity</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a909a]" />
                    <Input value={practitionerQuery} onChange={(event) => setPractitionerQuery(event.target.value)} placeholder="Search practitioners" className="pl-9" />
                  </div>
                </div>
              }
            >
              <PractitionerActivityList
                rows={filteredPractitioners}
                isInitialLoading={isLoading && !data}
                onViewMealPlans={viewPractitionerMealPlans}
              />
            </SectionShell>
          </TabsContent>

          <TabsContent value="quality" className="mt-0 space-y-5 duration-300 animate-in fade-in-50 slide-in-from-bottom-1">
            <StatBand cols={3}>
              <StatCell
                label="Generated sample density"
                value={formatNumber(summary?.sampleMealPlans || 0)}
                icon={Sparkles}
                accent="text-[#7c6cf0]"
                definition="Number of recent genuine external practitioner plans loaded into this review sample."
                onAction={() => openTabDetails('quality', 'meal-plan-review-queue')}
              />
              <StatCell
                label="Meal volume sampled"
                value={formatNumber((data?.sampleMealPlans || []).reduce((total, sample) => total + sample.mealCount, 0))}
                icon={ChefHat}
                accent="text-[#0f9b6c]"
                definition="Total meals contained in the plans currently sampled for review."
                onAction={() => openTabDetails('quality', 'meal-plan-review-queue')}
              />
              <StatCell
                label="Latest sample"
                value={formatDateTime(data?.sampleMealPlans?.[0]?.generatedAt || data?.sampleMealPlans?.[0]?.createdAt)}
                icon={Clock3}
                accent="text-[#e08a1e]"
                small
                definition="Creation time of the most recent plan in the current review sample."
                onAction={() => openTabDetails('quality', 'meal-plan-review-queue')}
              />
            </StatBand>

            <SectionShell
              id="meal-plan-review-queue"
              title="Meal Plan Review Queue"
              eyebrow="Quality samples"
              action={
                <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-[18rem_12rem]">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a909a]" />
                    <Input
                      value={sampleQuery}
                      onChange={(event) => setSampleQuery(event.target.value)}
                      placeholder="Search meal plans"
                      className="pl-9"
                    />
                  </div>
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="border-[#e0e2e7]">
                      <Filter className="h-4 w-4 text-[#8a909a]" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sources</SelectItem>
                      {sourceOptions.map((source) => (
                        <SelectItem key={source} value={source}>
                          {formatSource(source)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              }
            >
              {samplePractitioner ? (
                <div className="mx-5 mt-5 flex flex-col gap-3 rounded-xl border border-[#bde5ef] bg-[#f2fbfd] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#0b6f89]">
                      Viewing meal plans for {samplePractitioner.nutritionistName}
                    </p>
                    <p className="mt-0.5 text-xs text-[#5a7b84]">
                      Latest 90 days · {formatNumber(summary?.sampleMealPlansMatching ?? summary?.sampleMealPlans ?? 0)} matching plan{(summary?.sampleMealPlansMatching ?? summary?.sampleMealPlans ?? 0) === 1 ? '' : 's'}.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSamplePractitioner(null)}>
                    View all practitioners
                  </Button>
                </div>
              ) : null}
              <div className="grid gap-4 p-5 xl:grid-cols-2">
                {filteredSamples.length > 0 ? (
                  filteredSamples.map((sample) => (
                    <SamplePlanCard
                      key={sample.draftId}
                      sample={sample}
                      selected={selectedSample?.draftId === sample.draftId}
                      onReview={openSample}
                    />
                  ))
                ) : (
                  <div className="col-span-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#e0e2e7] bg-[#f7f8fa] px-4 py-16 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eef6f8] text-[#0e97be]">
                      <ChefHat className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#14151a]">No sample meal plans found</p>
                      <p className="mt-1 text-sm text-[#9aa1ab]">Adjust the window, source filter, or search to see results.</p>
                    </div>
                  </div>
                )}
              </div>
            </SectionShell>
          </TabsContent>

          <TabsContent value="cohorts" className="mt-0 space-y-5 duration-300 animate-in fade-in-50 slide-in-from-bottom-1">
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e9f9fc] text-[#0e97be]">
                  <BarChart3 className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-heading text-lg font-bold tracking-tight text-[#14151a]">AI Meal-Plan Cohorts</h2>
                  <p className="text-sm text-[#8a909a]">Grouped by AI meal-plan model assignment</p>
                </div>
              </div>
              {(data?.cohorts || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#e0e2e7] bg-white px-4 py-16 text-center shadow-[0_1px_2px_rgba(15,36,44,0.04)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f2f2ff] text-[#5877c7]">
                    <BrainCircuit className="h-6 w-6" />
                  </span>
                  <p className="text-sm font-semibold text-[#14151a]">No cohorts to display</p>
                </div>
              ) : (
                <div className="grid gap-4 xl:grid-cols-3">
                  {(data?.cohorts || []).map((group) => (
                    <CohortColumn key={group.cohort} group={group} />
                  ))}
                </div>
              )}
            </section>
          </TabsContent>
          </Tabs>
        </div>
      </div>

      <MealPlanDetailDialog sample={selectedSample} open={detailOpen} onOpenChange={setDetailOpen} />
    </main>
  );
};

export default UsageQualityMetricsPage;
